import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { Button, CircularProgress } from '@material-ui/core'
import PropertyReviewModal from '../../PropertyDetail/PropertyReviewModal'
import { saveBillingGroup, getPropertyRoute } from '../../PropertyDetail'
import { V4_T } from '../../PropertyDetail/propertyDetailHelpers'
import { buildReviewQueue } from '../../PropertyDetail/propertyReviewQueue'
import { buildTerritoryPatchBody, applySavedFieldsToProperty } from '../PropertyOverview/propertySaveHelpers'
import { flagCategoryLabel, buildOpenFlagMap } from '../../PropertyDetail/propertyReviewFlags'
import { fetchIrrigationCrews } from '../../PropertyDetail/irrigationCrews'
import { SETUP_CHECKS, getFailingKeys, computeSetupStats } from './propertySetupStats'

const SERIF = "'Iowan Old Style', 'Palatino Linotype', Georgia, serif"

const formatShortDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const labelStyle = { fontFamily: 'monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: V4_T.muteSoft, textTransform: 'uppercase' }

// Dedicated setup-goals board over the same routing-properties dataset as
// /hmi/properties, but scoped to onboarding readiness: the six triage checks
// per property, worst-first, with a readiness dashboard and the audit trail's
// "last edited" stamp. Rows open the map-dominant review walkthrough pinned
// to the clicked property; saves go through the same territory PATCH (which
// writes the PropertySetupAudit trail server-side).
export function PropertySetupBoard ({ url, user }) {
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [latestAudit, setLatestAudit] = useState({})
  const [search, setSearch] = useState('')
  const [activeKeys, setActiveKeys] = useState(() => new Set())
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewInitialId, setReviewInitialId] = useState(null)
  // Open review flags ("someone else should look at this"), keyed by property.
  const [openFlags, setOpenFlags] = useState({})
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  // Portfolio-wide invoice-contact mismatches (batched), keyed by Aspire
  // PropertyID string -> { missingCount, missing: [{ name, email }] }.
  // Keys come straight off the server JSON (String(PropertyID)), so lookups
  // use String(propertyId) to read like the map that's built — cf. openFlags,
  // whose client-built map is keyed with Number().
  const [billingSummary, setBillingSummary] = useState({})
  const [billingOnly, setBillingOnly] = useState(false)

  const fetchAuditLatest = useCallback(async () => {
    try {
      const res = await axios.get(`${url}/api/hmi/property-setup/audit/latest`)
      setLatestAudit(res.data?.latest || {})
    } catch (err) {
      console.error('Failed to fetch setup audit summary:', err)
    }
  }, [url])

  const fetchFlags = useCallback(async () => {
    try {
      const res = await axios.get(`${url}/api/hmi/property-setup/flags?status=open`)
      setOpenFlags(buildOpenFlagMap(res.data?.flags))
    } catch (err) {
      console.error('Failed to fetch review flags:', err)
    }
  }, [url])

  const fetchBillingSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${url}/api/hmi/property-setup/invoice-contacts/summary`)
      setBillingSummary(res.data?.summary || {})
    } catch (err) {
      console.error('Failed to fetch invoice-contact summary:', err)
      setBillingSummary({})
    }
  }, [url])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await axios.get(`${url}/api/aspire/routing/properties`)
        if (!cancelled) setProperties(res.data || [])
      } catch (err) {
        console.error('Failed to fetch properties:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    fetchAuditLatest()
    fetchFlags()
    fetchBillingSummary()
    return () => { cancelled = true }
  }, [url, fetchAuditLatest, fetchFlags, fetchBillingSummary])

  const stats = useMemo(() => computeSetupStats(properties), [properties])
  const flaggedCount = Object.keys(openFlags).length
  // Count of on-board properties that have a billing gap (intersection — the
  // summary can include properties outside this board's routing-properties set).
  const billingGapCount = useMemo(
    () => properties.filter(p => billingSummary[String(p.propertyId)]).length,
    [properties, billingSummary]
  )

  // Same filter semantics as the review modal's triage bar (chips OR, search
  // narrows) plus the flagged-only toggle, then worst-first: most failing
  // checks at the top of the table.
  const rows = useMemo(() => {
    const queue = buildReviewQueue(properties, { search, activeKeys })
    return [...queue]
      .filter(p => !flaggedOnly || openFlags[Number(p.propertyId)])
      .filter(p => !billingOnly || billingSummary[String(p.propertyId)])
      .map(p => ({ property: p, failing: new Set(getFailingKeys(p)) }))
      .sort((a, b) =>
        (b.failing.size - a.failing.size) ||
        String(a.property.propertyName || a.property.name || '').localeCompare(String(b.property.propertyName || b.property.name || ''))
      )
  }, [properties, search, activeKeys, flaggedOnly, openFlags, billingOnly, billingSummary])

  // Real Irrigation-division crews for the review modal's "Assigned Irrigation
  // Tech" picker (was a name-keyed placeholder). See irrigationCrews.js.
  const [editCrews, setEditCrews] = useState([])
  useEffect(() => {
    let cancelled = false
    fetchIrrigationCrews(url)
      .then(crews => { if (!cancelled) setEditCrews(crews) })
      .catch(err => { if (!cancelled) { console.error('Failed to fetch irrigation crews:', err); setEditCrews([]) } })
    return () => { cancelled = true }
  }, [url])

  const billingPropertyOptions = useMemo(() => {
    const byId = new Map()
    properties.forEach(p => {
      const id = Number(p.aspirePropertyId || p.propertyId || p.PropertyID)
      if (!Number.isFinite(id) || id <= 0 || byId.has(id)) return
      byId.set(id, {
        propertyId: id,
        label: p.propertyName || p.name || p.companyName || `Property #${id}`,
        companyName: p.companyName || '',
        address: p.address1 || ''
      })
    })
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
  }, [properties])

  // Mirrors PropertyOverview.saveEditForProperty: territory PATCH + billing
  // group endpoint, local list update, errors re-thrown for the form. Also
  // stamps the audit column optimistically (server writes the durable entry).
  const saveEditForProperty = useCallback(async (aspirePropertyId, payload, opts = {}) => {
    if (aspirePropertyId == null || !payload) return
    const body = buildTerritoryPatchBody(payload)
    if (Object.keys(body).length > 0) {
      try {
        await axios.patch(`${url}/api/hmi/territory/properties/${aspirePropertyId}`, body)
        setProperties(prev => prev.map(p =>
          String(p.propertyId) === String(aspirePropertyId) ? applySavedFieldsToProperty(p, body) : p
        ))
        setLatestAudit(prev => ({
          ...prev,
          [aspirePropertyId]: { name: user?.name || '', email: user?.email || '', at: new Date().toISOString() }
        }))
      } catch (err) {
        console.error('Failed to save property edit:', err)
        throw err
      }
    }
    if (payload.billingGroup) {
      const res = await saveBillingGroup(axios, url, payload.billingGroup, opts.force)
      const billingGroup = res.data || null
      setProperties(prev => prev.map(p =>
        String(p.propertyId) === String(aspirePropertyId) ? { ...p, billingGroup } : p
      ))
    }
  }, [url, user])

  const toggleKey = (key) => {
    setActiveKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const openReview = (propertyId) => {
    setReviewInitialId(propertyId)
    setReviewOpen(true)
  }

  const tileStyle = (selected) => ({
    background: selected ? V4_T.green : '#fff',
    border: `1px solid ${selected ? V4_T.green : V4_T.line}`,
    borderRadius: 12,
    padding: '12px 14px',
    textAlign: 'left',
    cursor: 'pointer',
    minWidth: 132,
    boxShadow: '0 1px 6px rgba(28,36,25,0.06)'
  })

  return (
    <div style={{ minHeight: '100vh', background: V4_T.surface, padding: '22px 26px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontFamily: SERIF, fontSize: 28, fontWeight: 700, color: V4_T.ink }}>Property Setup</h1>
        <span style={{ fontSize: 13, color: V4_T.mute }}>Onboarding readiness across the maintenance portfolio</span>
        <a href="/hmi/properties" style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 600, color: V4_T.green }}>Property overview →</a>
      </div>

      {/* Dashboard band */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch', margin: '16px 0 18px' }}>
        <div style={{ background: '#fff', border: `1px solid ${V4_T.line}`, borderRadius: 12, padding: '12px 18px', minWidth: 190, boxShadow: '0 1px 6px rgba(28,36,25,0.06)' }}>
          <div style={labelStyle}>Fully set up</div>
          <div style={{ fontFamily: SERIF, fontSize: 34, fontWeight: 700, color: V4_T.green, lineHeight: 1.1 }}>{`${stats.fullyReadyPct}%`}</div>
          <div style={{ fontSize: 12, color: V4_T.mute }}>{`${stats.fullyReady} of ${stats.total} properties`}</div>
        </div>
        <div style={{ background: '#fff', border: `1px solid ${V4_T.line}`, borderRadius: 12, padding: '12px 18px', minWidth: 210, boxShadow: '0 1px 6px rgba(28,36,25,0.06)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8 }}>
          <div style={labelStyle}>{`Checks passing · ${stats.overallPct}%`}</div>
          <div style={{ height: 8, borderRadius: 999, background: V4_T.line, overflow: 'hidden' }}>
            <div style={{ width: `${stats.overallPct}%`, height: '100%', background: V4_T.green, transition: 'width .3s' }} />
          </div>
          <div style={{ fontSize: 11.5, color: V4_T.muteSoft }}>Across all six setup goals</div>
        </div>
        {SETUP_CHECKS.map(({ key, label }) => {
          const selected = activeKeys.has(key)
          const failing = stats.failingCounts[key] || 0
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggleKey(key)}
              aria-pressed={selected}
              aria-label={label}
              style={tileStyle(selected)}
            >
              <div style={{ ...labelStyle, color: selected ? 'rgba(255,255,255,0.85)' : V4_T.muteSoft }}>{label}</div>
              <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 700, color: selected ? '#fff' : (failing ? V4_T.rust : V4_T.green), lineHeight: 1.15 }}>{failing}</div>
              <div style={{ fontSize: 11, color: selected ? 'rgba(255,255,255,0.85)' : V4_T.muteSoft }}>{failing === 1 ? 'property to fix' : 'properties to fix'}</div>
            </button>
          )
        })}
        <button
          type="button"
          onClick={() => setFlaggedOnly(prev => !prev)}
          aria-pressed={flaggedOnly}
          aria-label="Flagged for review"
          style={{ ...tileStyle(flaggedOnly), ...(flaggedOnly ? { background: V4_T.rust, borderColor: V4_T.rust } : {}) }}
        >
          <div style={{ ...labelStyle, color: flaggedOnly ? 'rgba(255,255,255,0.85)' : V4_T.muteSoft }}>Flagged for review</div>
          <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 700, color: flaggedOnly ? '#fff' : (flaggedCount ? V4_T.rust : V4_T.green), lineHeight: 1.15 }}>{flaggedCount}</div>
          <div style={{ fontSize: 11, color: flaggedOnly ? 'rgba(255,255,255,0.85)' : V4_T.muteSoft }}>{flaggedCount === 1 ? 'open flag' : 'open flags'}</div>
        </button>
        <button
          type="button"
          onClick={() => setBillingOnly(prev => !prev)}
          aria-pressed={billingOnly}
          aria-label="Billing contacts"
          style={{ ...tileStyle(billingOnly), ...(billingOnly ? { background: V4_T.rust, borderColor: V4_T.rust } : {}) }}
        >
          <div style={{ ...labelStyle, color: billingOnly ? 'rgba(255,255,255,0.85)' : V4_T.muteSoft }}>Billing contacts</div>
          <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 700, color: billingOnly ? '#fff' : (billingGapCount ? V4_T.rust : V4_T.green), lineHeight: 1.15 }}>{billingGapCount}</div>
          <div style={{ fontSize: 11, color: billingOnly ? 'rgba(255,255,255,0.85)' : V4_T.muteSoft }}>{billingGapCount === 1 ? 'billing gap' : 'billing gaps'}</div>
        </button>
      </div>

      {/* Filter strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <input
          type="text"
          aria-label="Search properties"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, client, community…"
          style={{ width: 280, padding: '7px 10px', border: `1px solid ${V4_T.line}`, borderRadius: 6, fontSize: 13, background: '#fff' }}
        />
        <span style={{ fontSize: 12, color: V4_T.muteSoft }}>{`${rows.length} of ${stats.total} shown · worst first`}</span>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: `1px solid ${V4_T.line}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 6px rgba(28,36,25,0.06)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1.4fr) minmax(150px, 1fr) repeat(6, 44px) 90px minmax(150px, 1fr) 110px', gap: 10, alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${V4_T.line}`, background: V4_T.surface2 }}>
          <span style={labelStyle}>Property</span>
          <span style={labelStyle}>Route · Day</span>
          {SETUP_CHECKS.map(check => (
            <span key={check.key} style={{ ...labelStyle, fontSize: 8.5, textAlign: 'center', lineHeight: 1.25 }} title={check.label}>
              {check.label.split(' ')[0]}
            </span>
          ))}
          <span style={{ ...labelStyle, textAlign: 'center' }}>Ready</span>
          <span style={labelStyle}>Last edited</span>
          <span />
        </div>

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <CircularProgress size={28} />
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: V4_T.muteSoft, fontSize: 13 }}>No properties match</div>
        )}

        {!loading && rows.map(({ property, failing }) => {
          const name = property.propertyName || property.name || `Property #${property.propertyId}`
          const readyCount = SETUP_CHECKS.length - failing.size
          const audit = latestAudit[property.propertyId]
          const flag = openFlags[Number(property.propertyId)]
          const route = getPropertyRoute(property)
          const day = (property.scheduledDays || []).map(d => String(d).slice(0, 3)).join(' · ')
          return (
            <div key={property.propertyId} data-testid="setup-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1.4fr) minmax(150px, 1fr) repeat(6, 44px) 90px minmax(150px, 1fr) 110px', gap: 10, alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${V4_T.line}` }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: V4_T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                  {flag && (
                    <span
                      title={flag.notes || flagCategoryLabel(flag.category)}
                      style={{ flexShrink: 0, fontFamily: 'monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: '#fff', background: V4_T.rust, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}
                    >
                      {flagCategoryLabel(flag.category)}
                    </span>
                  )}
                  {billingSummary[String(property.propertyId)] && (
                    <span
                      title={billingSummary[String(property.propertyId)].missing.map(m => `${m.name} · ${m.email}`).join('\n')}
                      style={{ flexShrink: 0, fontFamily: 'monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: '#fff', background: V4_T.rust, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}
                    >
                      {`${billingSummary[String(property.propertyId)].missingCount} billing`}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: V4_T.muteSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {[property.companyName, property.address1].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ fontSize: 12.5, color: V4_T.ink2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {[route, day].filter(Boolean).join(' · ') || '—'}
              </div>
              {SETUP_CHECKS.map(check => {
                const bad = failing.has(check.key)
                return (
                  <span
                    key={check.key}
                    title={`${check.label}: ${bad ? 'needs attention' : 'done'}`}
                    style={{ justifySelf: 'center', width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: bad ? V4_T.rustSoft : V4_T.green, color: bad ? V4_T.rust : '#fff', border: bad ? `1.5px solid ${V4_T.rust}55` : 'none' }}
                  >
                    {bad ? '!' : '✓'}
                  </span>
                )
              })}
              <span style={{ justifySelf: 'center', fontFamily: 'monospace', fontSize: 12.5, fontWeight: 700, color: failing.size === 0 ? V4_T.green : V4_T.ink2 }}>
                {`${readyCount}/${SETUP_CHECKS.length}`}
              </span>
              <div style={{ fontSize: 12, color: V4_T.ink2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {audit?.name
                  ? <>{audit.name}<span style={{ color: V4_T.muteSoft }}>{audit.at ? ` · ${formatShortDate(audit.at)}` : ''}</span></>
                  : <span style={{ color: V4_T.muteSoft }}>—</span>}
              </div>
              <Button size="small" variant="outlined" aria-label={`review ${name}`} onClick={() => openReview(property.propertyId)}>
                Review
              </Button>
            </div>
          )
        })}
      </div>

      <PropertyReviewModal
        open={reviewOpen}
        onClose={() => { setReviewOpen(false); fetchAuditLatest(); fetchFlags() }}
        properties={properties}
        url={url}
        crews={editCrews}
        propertyOptions={billingPropertyOptions}
        onSave={saveEditForProperty}
        initialPropertyId={reviewInitialId}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(PropertySetupBoard)
